<script setup lang="ts">
import type { MDBPrismeCaptureSpore } from '@/types/prisme/capturespore'
import { notifyError, to_wd_dd_MMMM_yyyy, to_dd_MM } from '@/utils/helpers'
import { addDays, startOfWeek } from 'date-fns'



const theme = useTheme()
const display = useDisplay()
// True only on real touch-primary devices (phones/tablets), independent of window width.
// A narrow desktop window keeps keyboard navigation; a touchscreen disables it.
const isTouchDevice = ref(false)
const storeU = useUtilisateurStore()
const route = useRoute()
const router = useRouter()
const dialogConfirm = useTemplateRef('dialogConfirm')

const isLoading = ref(false)
const isSaving = ref(false)
const canEdit = ref(false)
const headerCollapsed = ref(false)

// ── Culture ──────────────────────────────────────────────
const showDialogCulture = ref(false)
const cultureSélectionnée = ref('')
const codeCulture = ref('')
const maladies = ref<string[]>([])
const maladiesDisplay = ref<string[]>([])
const maladiesDisplayMobile = ref<string[]>([])
const cultures = ref<string[]>([])

// ── Week navigation ───────────────────────────────────────
const weekSelector = useTemplateRef('weekSelector')
const selectedWeek = ref(new Date(storeU.journéeSélectionnée))
const numberOfDays = ref(7)

// ── Data ──────────────────────────────────────────────────
const capteurs = ref<MDBPrismeCaptureCapteur[]>([])

// ── Client filter ─────────────────────────────────────────
const STORAGE_KEY = 'captures-spores-selected-clients'

// ── Current site context ─────────────────────────────────
const isPRSM = computed(() => storeU.siteSélectionné.lettresDappel === 'PRSM')
const prsmLD = computed(() => storeU.siteSélectionné.lettresDappelPrisme || storeU.siteSélectionné.lettresDappel)

// ── URL highlight ───────────────────────────────────────
const highlightNom = computed(() => (route.query.capteur as string | undefined) ?? '')

const selectedClients = ref<string[]>((() => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]') } catch { return [] }
})())

const clientsDisponibles = computed(() => {
  const seen = new Set<string>()
  return capteurs.value
    .map(c => c.dernierClientNom)
    .filter(n => { if (seen.has(n)) return false; seen.add(n); return true })
})

const capteursFiltés = computed(() =>
  selectedClients.value.length === 0
    ? capteurs.value
    : capteurs.value.filter(c => selectedClients.value.includes(c.dernierClientNom))
)

watch(selectedClients, val => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(val))
}, { deep: true })

watch(capteursFiltés, async (list) => {
  if (!highlightNom.value || list.length === 0) return
  await nextTick()
  const match = list.find(c => c.nom === highlightNom.value)
  if (!match) return
  document.getElementById(`capteur-row-${match.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}, { immediate: false })

function toggleClient(client: string) {
  const idx = selectedClients.value.indexOf(client)
  if (idx >= 0) selectedClients.value.splice(idx, 1)
  else selectedClients.value.push(client)
}

function clearClientFilter() {
  selectedClients.value = []
}

function onTableScroll(e: Event) {
  if (display.smAndDown.value)
    headerCollapsed.value = (e.target as HTMLElement).scrollTop > 30
}

// dates: sorted list of ISO date strings shown as columns
const dates = ref<string[]>([])

// records: flat list from API — used as source of truth
const records = ref<MDBPrismeCaptureSpore[]>([])

// pending edits: key = `${capteurId}|${dateStr}|${nomMaladie}`, value = partial record
declare interface PendingEdit {
  capteurId: string
  date: string
  nomMaladie: string
  valeur: number
  existingId: string // '' = new record
}
const pendingEdits = ref(new Map<string, PendingEdit>())

const hasChanges = computed(() => pendingEdits.value.size > 0)

// ── Polling ───────────────────────────────────────────────
let pollInterval: ReturnType<typeof setInterval> | null = null

// ── Computed week days ────────────────────────────────────
const weekDays = computed((): string[] => {
  const start = startOfWeek(selectedWeek.value ?? new Date(), { weekStartsOn: 0 })
  return Array.from({ length: numberOfDays.value }, (_, i) => {
    const d = addDays(start, i)
    d.setHours(12, 0, 0, 0)
    return d.toISOString()
  })
})

// Only show Sunday (0), Tuesday (2), Thursday (4)
const datesFiltrées = computed(() =>
  dates.value.filter(iso => [0, 2, 4].includes(new Date(iso).getDay()))
)

watch(selectedWeek, async () => {
  await nextTick()
  await loadData()
})

watch(codeCulture, async () => {
  await loadData()
})

// ── Lifecycle ─────────────────────────────────────────────
onMounted(() => {
  isTouchDevice.value = window.matchMedia('(hover: none) and (pointer: coarse)').matches
  cultures.value = []
  if (storeU.siteAAccès('prisme.ogn.captures') && storeU.aAccèsPermission('site.PRSM.get.prsm.ogn.captures.data')) cultures.value.push('Oignon')
  if (storeU.siteAAccès('prisme.lai.captures') && storeU.aAccèsPermission('site.PRSM.get.prsm.lai.captures.data')) cultures.value.push('Laitue')
  if (storeU.siteAAccès('prisme.pdt.captures') && storeU.aAccèsPermission('site.PRSM.get.prsm.pdt.captures.data')) cultures.value.push('Pomme de terre')
  if (storeU.siteAAccès('prisme.vig.captures') && storeU.aAccèsPermission('site.PRSM.get.prsm.vig.captures.data')) cultures.value.push('Vigne')
  if (storeU.siteAAccès('prisme.cru.captures') && storeU.aAccèsPermission('site.PRSM.get.prsm.cru.captures.data')) cultures.value.push('Crucifère')

  if (cultures.value.length === 0) {
    notifyError("Vous n'avez accès à aucune culture.")
    return
  }

  confirmCulture(cultures.value[0])

  // Poll every 30s for multi-user sync (only refreshes non-pending records)
  pollInterval = setInterval(pollData, 30_000)
})

onUnmounted(() => {
  if (pollInterval) clearInterval(pollInterval)
})

// ── Culture helpers ───────────────────────────────────────
async function confirmCulture(culture: string) {
  if (hasChanges.value) {
    const ok = await dialogConfirm.value?.open('Vous avez des modifications non enregistrées. Voulez-vous vraiment changer de culture ?')
    if (!ok?.valueOf()) return
  }
  switch (culture) {
    case 'Oignon':
      codeCulture.value = 'ogn'
      maladies.value = ['Stemphylium vesicarium', 'Peronospora destructor', 'Botrytis squamosa']
      maladiesDisplay.value = ['Stemphylium', 'Mildiou', 'Botrytis']
      maladiesDisplayMobile.value = ['Stemp.', 'Mil.', 'Bot.']
      break
    case 'Laitue':
      codeCulture.value = 'lai'
      maladies.value = ['Bremia lactucae']
      maladiesDisplay.value = ['Mildiou']
      maladiesDisplayMobile.value = ['Mil.']
      break
    case 'Pomme de terre':
      codeCulture.value = 'pdt'
      maladies.value = ['Phytophtora infestans', 'Alternaria solani', 'Alternaria alternata']
      maladiesDisplay.value = ['Mildiou', 'Alternaria solani', 'Alternaria alternata']
      maladiesDisplayMobile.value = ['Mil.', 'Alt. sol.', 'Alt. alt.']
      break
    case 'Vigne':
      codeCulture.value = 'vig'
      maladies.value = ['Botrytis cinerea', 'Erysiphe necator', 'Plasmopara viticola A', 'Plasmopara viticola B']
      maladiesDisplay.value = ['Pourriture grise', 'Oïdium', 'Mildiou A', 'Mildiou B']
      maladiesDisplayMobile.value = ['Pour.', 'Oïdium', 'Mil. A', 'Mil. B']
      break
    case 'Crucifère':
      codeCulture.value = 'cru'
      maladies.value = ['Alternaria brassicicola', 'Alternaria brassicae']
      maladiesDisplay.value = ['Alternariose brassicicola', 'Alternariose brassicae']
      maladiesDisplayMobile.value = ['Alt. bras.', 'Alt. bras.']
      break
    default:
      notifyError(`Culture inconnue: ${culture}`)
      return
  }
  cultureSélectionnée.value = culture
  showDialogCulture.value = false
  pendingEdits.value.clear()
  await loadData()
}

// ── Data loading ──────────────────────────────────────────
async function loadData() {
  if (hasChanges.value) {
    const ok = await dialogConfirm.value?.open('Vous avez des modifications non enregistrées. Voulez-vous vraiment recharger ?')
    if (!ok?.valueOf()) return
    pendingEdits.value.clear()
  }
  isLoading.value = true
  try {
    const siteCS = storeU.siteSélectionné.lettresDappel
    canEdit.value = storeU.siteSélectionné.lettresDappel === 'PRSM' && storeU.aAccèsPermission(`site.PRSM.put.prsm.${codeCulture.value}.captures.data`)

    const [capteursRes, capturesRes] = await Promise.all([
      apiProphet.get(`prsm/${siteCS}/captures/capteurs`),
      apiProphet.get(`prsm/${siteCS}/captures/${codeCulture.value}`, {
        params: { date: weekDays.value[0], nombreJours: numberOfDays.value }
      })
    ])

    capteurs.value = (capteursRes.data as MDBPrismeCaptureCapteur[]).filter(c => !c.désactivé && (c.cultures.length === 0 || c.cultures.includes(cultureSélectionnée.value))).sort((a, b) => {
      if (!isPRSM.value) {
        const aMatch = a.dernierClientNom === prsmLD.value ? 0 : 1
        const bMatch = b.dernierClientNom === prsmLD.value ? 0 : 1
        if (aMatch !== bMatch) return aMatch - bMatch
      }
      return a.dernierRangOrdre - b.dernierRangOrdre
    })

    records.value = capturesRes.data as MDBPrismeCaptureSpore[]
    dates.value = weekDays.value
  } catch (e) {
    notifyError(e)
  } finally {
    isLoading.value = false
  }
}

/** Silent poll — merges new server data without overwriting pending edits */
async function pollData() {
  if (isLoading.value || isSaving.value || !codeCulture.value) return
  try {
    const siteCS = storeU.siteSélectionné.lettresDappel
    const res = await apiProphet.get(`prsm/${siteCS}/captures/${codeCulture.value}`, {
      params: { date: weekDays.value[0], nombreJours: numberOfDays.value }
    })
    const fresh = res.data as MDBPrismeCaptureSpore[]
    // Only update records that are NOT pending edits
    for (const r of fresh) {
      const key = cellKey(r.capteurId, r.date, r.nomMaladie)
      if (!pendingEdits.value.has(key)) {
        const idx = records.value.findIndex(
          x => x.capteurId === r.capteurId && x.date === r.date && x.nomMaladie === r.nomMaladie
        )
        if (idx >= 0) records.value[idx] = r
        else records.value.push(r)
      }
    }
  } catch {
    // silent
  }
}

// ── Cell helpers ──────────────────────────────────────────
function cellKey(capteurId: string, date: string, nomMaladie: string): string {
  return `${capteurId}|${isoDay(date)}|${nomMaladie}`
}

/** Normalize to YYYY-MM-DD for comparison */
function isoDay(iso: string): string {
  return iso.substring(0, 10)
}

function getRecord(capteurId: string, date: string, nomMaladie: string): MDBPrismeCaptureSpore | undefined {
  const day = isoDay(date)
  return records.value.find(
    r => r.capteurId === capteurId && isoDay(r.date) === day && r.nomMaladie === nomMaladie
  )
}

function getCellValue(capteurId: string, date: string, nomMaladie: string): number | null {
  const key = cellKey(capteurId, date, nomMaladie)
  if (pendingEdits.value.has(key)) return pendingEdits.value.get(key)!.valeur
  const rec = getRecord(capteurId, date, nomMaladie)
  return rec !== undefined ? rec.valeur : null
}

function isPending(capteurId: string, date: string, nomMaladie: string): boolean {
  return pendingEdits.value.has(cellKey(capteurId, date, nomMaladie))
}

function onCellInput(capteurId: string, date: string, nomMaladie: string, event: Event) {
  const input = event.target as HTMLInputElement
  const sanitized = input.value.replace(/[^\d.]/g, '')
  if (input.value !== sanitized) input.value = sanitized
  const num = parseFloat(sanitized)
  if (isNaN(num)) return
  const key = cellKey(capteurId, date, nomMaladie)
  const existing = getRecord(capteurId, date, nomMaladie)
  pendingEdits.value.set(key, {
    capteurId,
    date,
    nomMaladie,
    valeur: num,
    existingId: existing?.id ?? ''
  })
}

function activateCell(capteurId: string, date: string, nomMaladie: string) {
  const key = cellKey(capteurId, date, nomMaladie)
  if (!pendingEdits.value.has(key)) {
    const existing = getRecord(capteurId, date, nomMaladie)
    pendingEdits.value.set(key, {
      capteurId,
      date,
      nomMaladie,
      valeur: existing?.valeur ?? 0,
      existingId: existing?.id ?? ''
    })
  }
  nextTick(() => {
    const el = document.getElementById(key) as HTMLInputElement | null
    if (el) { el.focus(); el.select() }
  })
}

// ── Keyboard navigation ───────────────────────────────────
function focusCell(ci: number, di: number, mi: number) {
  const key = cellKey(capteursFiltés.value[ci].id, datesFiltrées.value[di], maladies.value[mi])
  nextTick(() => {
    const input = document.getElementById(key) as HTMLInputElement | null
    if (input) { input.focus(); input.select(); return }
    const btn = document.getElementById(`btn-${key}`) as HTMLElement | null
    if (btn) btn.focus()
  })
}

function activateCellWithDigit(ci: number, di: number, mi: number, digit: number) {
  const capteurId = capteursFiltés.value[ci].id
  const date = datesFiltrées.value[di]
  const maladie = maladies.value[mi]
  const existing = getRecord(capteurId, date, maladie)
  const key = cellKey(capteurId, date, maladie)
  pendingEdits.value.set(key, { capteurId, date, nomMaladie: maladie, valeur: digit, existingId: existing?.id ?? '' })
  nextTick(() => {
    const el = document.getElementById(key) as HTMLInputElement | null
    // Place cursor at end so subsequent digits append rather than replace
    if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length) }
  })
}

function onCellKeyDown(event: KeyboardEvent, ci: number, di: number, mi: number) {
  // Keyboard navigation is for physical-keyboard devices — disabled on touch devices.
  if (isTouchDevice.value) return
  const M = maladies.value.length
  const D = datesFiltrées.value.length
  const C = capteursFiltés.value.length
  const isOnButton = (event.target as HTMLElement).tagName === 'BUTTON'

  // Digit key on empty-cell button → activate with that digit
  if (isOnButton && /^\d$/.test(event.key)) {
    event.preventDefault()
    activateCellWithDigit(ci, di, mi, parseInt(event.key))
    return
  }

  let nc = ci, nd = di, nm = mi

  switch (event.key) {
    case 'Delete':
      // Mirror the delete button — remove the saved capture for this cell.
      event.preventDefault()
      deleteCapture(capteursFiltés.value[ci].id, datesFiltrées.value[di], maladies.value[mi])
      return
    case 'Tab':
      event.preventDefault()
      if (event.shiftKey) {
        nm--; if (nm < 0) { nm = M - 1; nd--; if (nd < 0) { nd = D - 1; nc = (nc - 1 + C) % C } }
      } else {
        nm++; if (nm >= M) { nm = 0; nd++; if (nd >= D) { nd = 0; nc = (nc + 1) % C } }
      }
      break
    case 'Enter':
      event.preventDefault()
      if (isOnButton) { activateCell(capteursFiltés.value[ci].id, datesFiltrées.value[di], maladies.value[mi]); return }
      nc = (nc + 1) % C  // Enter on input → move down to next capteur
      break
    case 'ArrowRight':
      event.preventDefault()
      nm++; if (nm >= M) { nm = 0; nd = Math.min(nd + 1, D - 1) }
      break
    case 'ArrowLeft':
      event.preventDefault()
      nm--; if (nm < 0) { nm = M - 1; nd = Math.max(nd - 1, 0) }
      break
    case 'ArrowDown':
      event.preventDefault()
      nc = Math.min(nc + 1, C - 1)
      break
    case 'ArrowUp':
      event.preventDefault()
      nc = Math.max(nc - 1, 0)
      break
    default:
      return
  }

  focusCell(nc, nd, nm)
}

// ── Save ──────────────────────────────────────────────────
async function saveData() {
  if (!hasChanges.value) return
  isSaving.value = true
  try {
    const siteCS = storeU.siteSélectionné.lettresDappel

    for (const [, edit] of pendingEdits.value) {
      const cap = capteurs.value.find((c: MDBPrismeCaptureCapteur) => c.id === edit.capteurId)

      const payload: MDBPrismeCaptureSpore = {
        id: edit.existingId,
        date: edit.date,
        codeCulture: codeCulture.value,
        champId: cap?.dernierChampId ?? '',
        capteurId: edit.capteurId,
        nomMaladie: edit.nomMaladie,
        valeur: edit.valeur,
        latitude: cap?.dernièreLatitude ?? 0,
        longitude: cap?.dernièreLongitude ?? 0,
        sortOrder: cap?.dernierRangOrdre ?? 0
      }

      if (edit.existingId === '') {
        await apiProphet.post(`prsm/${siteCS}/captures/${codeCulture.value}`, payload)
      } else {
        await apiProphet.put(`prsm/${siteCS}/captures/${codeCulture.value}/${edit.existingId}`, payload)
      }
    }

    Notify.success('Enregistré avec succès')
    pendingEdits.value.clear()
    await silentRefresh()
  } catch (e) {
    notifyError(e)
  } finally {
    isSaving.value = false
  }
}

async function silentRefresh() {
  try {
    const siteCS = storeU.siteSélectionné.lettresDappel
    const res = await apiProphet.get(`prsm/${siteCS}/captures/${codeCulture.value}`, {
      params: { date: weekDays.value[0], nombreJours: numberOfDays.value }
    })
    records.value = res.data as MDBPrismeCaptureSpore[]
  } catch (e) {
    notifyError(e)
  }
}

async function deleteCapture(capteurId: string, date: string, nomMaladie: string) {
  const key = cellKey(capteurId, date, nomMaladie)
  const record = getRecord(capteurId, date, nomMaladie)
  // Unsaved value only → just discard the pending edit, no API call / confirm.
  if (!record?.id) {
    pendingEdits.value.delete(key)
    return
  }
  const ok = await dialogConfirm.value?.open('Supprimer cette capture ? Cette action est irréversible.')
  if (!ok?.valueOf()) return
  try {
    const siteCS = storeU.siteSélectionné.lettresDappel
    await apiProphet.delete(`prsm/${siteCS}/captures/${codeCulture.value}/${record.id}`)
    const idx = records.value.findIndex(r => r.id === record.id)
    if (idx >= 0) records.value.splice(idx, 1)
    pendingEdits.value.delete(key)
    Notify.success('Capture supprimée')
  } catch (e) {
    notifyError(e)
  }
}

// ── Display helpers ───────────────────────────────────────
function displayDate(iso: string): string {
  const d = new Date(iso)
  const wd = d.toLocaleDateString('fr-CA', { weekday: 'short' })
  return `${wd} ${to_dd_MM(d)}`
}
function displayDateFull(iso: string): string {
  return to_wd_dd_MMMM_yyyy(new Date(iso))
}

function goToCarte(capteur: MDBPrismeCaptureCapteur) {
  if (!capteur.dernièreLatitude || !capteur.dernièreLongitude) return
  router.push({
    path: '/carte',
    query: {
      lat: String(capteur.dernièreLatitude),
      lng: String(capteur.dernièreLongitude),
      zoom: '16',
      layer: 'spores',
    }
  })
}

// ── Print ─────────────────────────────────────────────────
function print() {
  const printWindow = window.open('', '_blank')
  if (printWindow) {
    printWindow.document.open()
    printWindow.document.write(generateReportHTML())
    printWindow.document.close()
  }
}

function generateReportHTML(): string {
  const title = `Captures de spores — ${cultureSélectionnée.value}`
  const weekRange = datesFiltrées.value.length
    ? `${displayDate(datesFiltrées.value[0])} → ${displayDate(datesFiltrées.value[datesFiltrées.value.length - 1])}`
    : ''
  const generatedAt = new Date().toLocaleString('fr-CA')

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${title}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 20px; font-size: 11px; }
    h3 { font-size: 13px; margin-bottom: 10px; }
    table { border-collapse: collapse; width: auto; }
    th, td { border: 1px solid #ccc; padding: 3px 6px; text-align: center; white-space: nowrap; }
    .col-producteur { text-align: left; width: 1%; }
    .col-champ { text-align: left; width: 1%; }
    .date-header { background-color: #2D6A4F; color: white; font-weight: bold; padding: 4px 8px; }
    .maladie-header { background-color: #e8f5e9; font-size: 10px; font-weight: 600; }
    .val-zero { color: #bbb; }
    .val-positive { font-weight: bold; }
    .val-empty { color: #ddd; }
    @media print { body { margin: 10px; } }
  </style>
</head>
<body>
  <h3>${title} — ${weekRange} — Généré le ${generatedAt}</h3>
  <table>
    <thead>
      <tr>
        <th class="col-producteur" rowspan="2">Producteur</th>
        <th class="col-champ" rowspan="2">Champ</th>
        ${datesFiltrées.value.map(date =>
          `<th colspan="${maladies.value.length}" class="date-header">${displayDate(date)}</th>`
        ).join('')}
      </tr>
      <tr>
        ${datesFiltrées.value.map(() =>
          maladiesDisplay.value.map(m => `<th class="maladie-header">${m}</th>`).join('')
        ).join('')}
      </tr>
    </thead>
    <tbody>
      ${capteursFiltés.value.map(capteur => `
        <tr>
          <td class="col-producteur"><strong>${capteur.dernierClientNom}</strong></td>
          <td class="col-champ">${capteur.dernierChampNom}</td>
          ${datesFiltrées.value.map(date =>
            maladies.value.map(maladie => {
              const val = getCellValue(capteur.id, date, maladie)
              if (val === null) return `<td class="val-empty">—</td>`
              if (val === 0) return `<td class="val-zero">0</td>`
              return `<td class="val-positive">${val}</td>`
            }).join('')
          ).join('')}
        </tr>`
      ).join('')}
    </tbody>
  </table>
</body>
</html>`
}
</script>

<template>
  <v-card class="mx-4 my-4" rounded="xl" :loading="isLoading || isSaving" style="height: calc(100vh - var(--v-layout-top) - var(--v-layout-bottom) - 32px); display: flex; flex-direction: column;">

    <!-- Page header -->
    <div
      class="page-header px-4 pt-4 pb-3"
      :class="{ 'header-collapsed': headerCollapsed && display.smAndDown }"
      :style="{
        background: theme.global.name.value === 'dark'
          ? 'rgba(var(--v-theme-surface-variant), 0.25)'
          : 'rgba(var(--v-theme-primary), 0.04)'
      }"
    >
      <!-- Row 1: avatar + title  |  action buttons -->
      <div class="d-flex align-center justify-space-between" style="gap: 8px;">
        <div class="d-flex align-center" style="gap: 10px; min-width: 0;">
          <v-avatar :color="hasChanges && canEdit ? 'warning' : 'primary'" variant="tonal" size="40" rounded="lg" class="flex-shrink-0">
            <v-icon :icon="hasChanges && canEdit ? 'M17 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V7L17 3M19 19H5V5H16.17L19 7.83V19M12 12C10.34 12 9 13.34 9 15S10.34 18 12 18 15 16.66 15 15 13.66 12 12 12M6 6H15V10H6V6Z' : 'M4,12H20C20,8.27 17.44,5.13 14,4.25C13.86,5.24 13,6 12,6C11,6 10.14,5.24 10,4.25C6.56,5.13 4,8.27 4,12M12,2A10,10 0 0,1 22,12A2,2 0 0,1 20,14H4A2,2 0 0,1 2,12A10,10 0 0,1 12,2M13.5,17H10.5L9.92,19L9.65,20H14.35L14.08,19L13.5,17M15,15L16,18.5L16.27,19.45L16.35,20C16.35,21.1 15.45,22 14.35,22H9.65L9.17,21.94C8.1,21.66 7.45,20.57 7.73,19.5L8,18.5L9,15H15M16,7A2,2 0 0,1 18,9A2,2 0 0,1 16,11A2,2 0 0,1 14,9A2,2 0 0,1 16,7M8,7A2,2 0 0,1 10,9A2,2 0 0,1 8,11A2,2 0 0,1 6,9A2,2 0 0,1 8,7Z'" size="24"/>
          </v-avatar>
          <div style="min-width: 0;">
            <div class="text-subtitle-1 font-weight-bold" style="line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">Captures de spores</div>
            <div v-if="!display.smAndDown" class="text-caption text-medium-emphasis">Données hebdomadaires par capteur</div>
          </div>
        </div>

        <!-- Action buttons -->
        <div class="d-flex align-center flex-shrink-0" style="gap: 6px;">
          <v-slide-x-reverse-transition>
            <v-btn
              v-if="hasChanges && canEdit"
              color="success" variant="flat"
              :icon="display.smAndDown ? true : undefined"
              :rounded="display.smAndDown ? 'circle' : 'pill'"
              :size="display.smAndDown ? 'small' : 'default'"
              :loading="isSaving"
              class="save-btn"
              @click="saveData">
              <v-icon icon="M17 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V7L17 3M19 19H5V5H16.17L19 7.83V19M12 12C10.34 12 9 13.34 9 15S10.34 18 12 18 15 16.66 15 15 13.66 12 12 12M6 6H15V10H6V6Z" :start="!display.smAndDown"/>
              <template v-if="!display.smAndDown">Enregistrer</template>
            </v-btn>
          </v-slide-x-reverse-transition>
          <!-- Refresh icon shown here only when the filter row won't render -->
          <v-btn
            v-if="clientsDisponibles.length <= 1"
            icon="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"
            color="primary" variant="tonal" size="small"
            :disabled="isLoading"
            @click="loadData"/>
          <v-btn
            icon="M18,3H6V7H18M19,12A1,1 0 0,1 18,11A1,1 0 0,1 19,10A1,1 0 0,1 20,11A1,1 0 0,1 19,12M16,19H8V14H16M19,8H5A3,3 0 0,0 2,11V17H6V21H18V17H22V11A3,3 0 0,0 19,8Z"
            color="primary" variant="tonal" size="small"
            :disabled="isLoading || capteursFiltés.length === 0"
            @click="print()"/>
        </div>
      </div>

      <!-- Row 2: culture chip + week selector -->
      <div class="d-flex align-center flex-wrap mt-2" style="gap: 8px;">
        <v-chip
          color="primary"
          variant="tonal"
          :size="display.smAndDown ? 'default' : 'large'"
          class="culture-chip"
          append-icon="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"
          style="cursor: pointer; font-weight: 600;"
          @click="showDialogCulture = true"
        >
          {{ cultureSélectionnée }}
        </v-chip>
        <v-divider vertical class="mx-1" style="height: 28px; align-self: center;"/>
        <WeekSelector ref="weekSelector" v-model="selectedWeek" v-model:number-of-days="numberOfDays"/>
      </div>

      <!-- Client filter: dropdown on mobile, chips on desktop -->
      <template v-if="clientsDisponibles.length > 1">
        <!-- Mobile: select dropdown -->
        <div v-if="display.smAndDown" class="mt-3 d-flex align-center" style="gap: 8px;">
          <v-select
            v-model="selectedClients"
            :items="clientsDisponibles"
            label="Afficher les fermes"
            multiple
            density="compact"
            variant="outlined"
            rounded="lg"
            hide-details
            clearable
            style="flex: 1;"
          >
            <template #selection="{ index }">
              <span v-if="index === 0" class="text-caption">
                {{ selectedClients.length === 0 || selectedClients.length === clientsDisponibles.length
                  ? 'Toutes les fermes'
                  : `${selectedClients.length} / ${clientsDisponibles.length} fermes` }}
              </span>
            </template>
          </v-select>
          <v-btn
            icon="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"
            color="primary" variant="tonal" size="small"
            :disabled="isLoading"
            @click="loadData"/>
        </div>
        <!-- Desktop: chips -->
        <div v-else class="d-flex align-center flex-wrap mt-3" style="gap: 6px; justify-content: space-between;">
          <span class="text-caption text-medium-emphasis me-2">Afficher :</span>
          <v-chip
            v-for="client in clientsDisponibles"
            :key="client"
            size="small"
            :color="selectedClients.length === 0 || selectedClients.includes(client) ? 'primary' : undefined"
            :variant="selectedClients.length === 0 || selectedClients.includes(client) ? 'tonal' : 'outlined'"
            style="cursor: pointer;"
            @click="toggleClient(client)"
          >
            {{ client }}
          </v-chip>
          <v-btn
            v-if="selectedClients.length > 0"
            size="x-small" variant="text" color="primary" class="ms-1"
            @click="clearClientFilter()">
            Tout afficher
          </v-btn>
          <v-btn
            icon="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"
            color="primary" variant="tonal" size="small" class="ms-auto"
            :disabled="isLoading"
            @click="loadData"/>
        </div>
      </template>
    </div>

    <v-divider/>

    <!-- Table container -->
    <div style="flex: 1 1 auto; overflow: auto;" class="pb-4" @scroll.passive="onTableScroll">
      <table class="spores-table">
        <thead>
          <!-- Row 1: date groups -->
          <tr>
            <th class="col-fixed col-capteur col-capteur-r1">Capteur</th>
            <template v-for="date in datesFiltrées" :key="`dh-${date}`">
              <th :colspan="maladies.length" class="date-header">
                <v-tooltip :text="displayDateFull(date)" location="bottom">
                  <template #activator="{ props }">
                    <span v-bind="props">{{ displayDate(date) }}</span>
                  </template>
                </v-tooltip>
              </th>
            </template>
          </tr>
          <!-- Row 2: maladie sub-headers -->
          <tr>
            <th class="col-fixed col-capteur col-capteur-r2"/>
            <template v-for="date in datesFiltrées" :key="`ds-${date}`">
              <th
                v-for="(m, mi) in maladiesDisplay"
                :key="`${date}-${mi}`"
                class="category-header"
                :style="display.smAndDown ? 'min-width: 40px;' : 'min-width: 68px;'"
              >
                {{ display.smAndDown ? (maladiesDisplayMobile[mi] ?? m) : m }}
              </th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(capteur, ci) in capteursFiltés"
            :id="`capteur-row-${capteur.id}`"
            :key="capteur.id"
            class="data-row"
            :class="{
              'row-current-site': !isPRSM && capteur.dernierClientNom === prsmLD,
              'row-highlighted': highlightNom && capteur.nom === highlightNom,
            }"
          >
            <!-- Fixed: capteur name -->
            <td class="col-fixed col-capteur capteur-cell">
              <!-- Mobile: single compact line -->
              <template v-if="display.smAndDown">
                <div class="d-flex align-center capteur-cell-mobile" style="gap: 4px; white-space: nowrap; overflow: hidden;">
                  <v-chip
                    :color="!isPRSM && capteur.dernierClientNom === prsmLD ? 'primary' : 'primary'"
                    size="x-small"
                    :variant="!isPRSM && capteur.dernierClientNom === prsmLD ? 'elevated' : 'flat'"
                    class="font-weight-bold flex-shrink-0"
                  >
                    <v-icon v-if="!isPRSM && capteur.dernierClientNom === prsmLD" start size="x-small">M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z</v-icon>
                    {{ capteur.dernierClientNom }}
                  </v-chip>
                  <span
                    class="text-caption capteur-nom-link"
                    :class="{ 'capteur-nom-link--clickable': capteur.dernièreLatitude && capteur.dernièreLongitude }"
                    style="overflow: hidden; text-overflow: ellipsis;"
                    @click.stop="goToCarte(capteur)"
                  >
                    <v-icon v-if="capteur.dernièreLatitude && capteur.dernièreLongitude" size="10" class="me-1" style="opacity:0.6;">M12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5M12,2A7,7 0 0,1 19,9C19,14.25 12,22 12,22C12,22 5,14.25 5,9A7,7 0 0,1 12,2M12,4A5,5 0 0,0 7,9C7,10 7,12 12,18.71C17,12 17,10 17,9A5,5 0 0,0 12,4Z</v-icon>{{ capteur.dernierChampNom }}
                  </span>
                </div>
              </template>
              <!-- Desktop: multi-line with chips -->
              <template v-else>
                <div class="d-flex align-center flex-wrap" style="gap: 3px; margin-bottom: 1px;">
                  <v-chip
                    :color="!isPRSM && capteur.dernierClientNom === prsmLD ? 'primary' : 'primary'"
                    size="x-small"
                    :variant="!isPRSM && capteur.dernierClientNom === prsmLD ? 'elevated' : 'flat'"
                    class="font-weight-bold"
                  >
                    <v-icon v-if="!isPRSM && capteur.dernierClientNom === prsmLD" start size="x-small">M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z</v-icon>
                    {{ capteur.dernierClientNom }}
                  </v-chip>
                  <v-chip
                    color="teal" size="x-small" variant="tonal" class="font-weight-bold">
                    <v-icon start size="x-small">M2,22V20C2,20 7,18 12,18C17,18 22,20 22,20V22H2M11.3,9.1C10.1,5.2 4,6.1 4,6.1C4,6.1 4.2,13.9 9.9,12.7C9.5,9.8 8,9 8,9C10.8,9 11,12.4 11,12.4V17C11.3,17 11.7,17 12,17C12.3,17 12.7,17 13,17V12.8C13,12.8 13,8.9 16,7.9C16,7.9 14,10.9 14,12.9C21,13.6 21,4 21,4C21,4 12.1,3 11.3,9.1Z</v-icon>
                    {{ capteur.dernierChampNom }}
                  </v-chip>
                </div>
                <div
                  class="text-caption capteur-nom-link"
                  :class="{ 'capteur-nom-link--clickable': capteur.dernièreLatitude && capteur.dernièreLongitude }"
                  style="white-space: nowrap; padding-left: 2px;"
                  @click.stop="goToCarte(capteur)"
                >
                  <v-icon v-if="capteur.dernièreLatitude && capteur.dernièreLongitude" size="11" class="me-1" style="opacity:0.6;">M12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5M12,2A7,7 0 0,1 19,9C19,14.25 12,22 12,22C12,22 5,14.25 5,9A7,7 0 0,1 12,2M12,4A5,5 0 0,0 7,9C7,10 7,12 12,18.71C17,12 17,10 17,9A5,5 0 0,0 12,4Z</v-icon>{{ capteur.nom }}
                </div>
              </template>
            </td>

            <!-- Data cells -->
            <template v-for="(date, di) in datesFiltrées" :key="`${capteur.id}-${date}`">
              <td
                v-for="(maladie, mi) in maladies"
                :key="`${capteur.id}-${date}-${mi}`"
                class="data-cell"
                :class="isTouchDevice ? {
                  'mobile-cell--positive': (getCellValue(capteur.id, date, maladie) ?? -1) > 0,
                  'mobile-cell--zero': getCellValue(capteur.id, date, maladie) === 0,
                  'mobile-cell--pending': isPending(capteur.id, date, maladie),
                } : {
                  'data-cell--higher': (getCellValue(capteur.id, date, maladie) ?? -1) > 0,
                  'data-cell--zero': getCellValue(capteur.id, date, maladie) === 0,
                }"
                @click.stop="isTouchDevice && canEdit ? activateCell(capteur.id, date, maladie) : undefined"
              >
                <!-- Touch devices: whole cell is colored; shows input when actively editing -->
                <template v-if="isTouchDevice">
                  <input
                    v-if="isPending(capteur.id, date, maladie) && canEdit"
                    :id="cellKey(capteur.id, date, maladie)"
                    type="text"
                    inputmode="numeric"
                    class="mobile-spore-input"
                    :value="getCellValue(capteur.id, date, maladie)"
                    @click.stop
                    @input="onCellInput(capteur.id, date, maladie, $event)"
                    @focus="($event.target as HTMLInputElement).select()"
                  />
                  <template v-else>
                    <span
                      v-if="getCellValue(capteur.id, date, maladie) !== null"
                      class="mobile-cell-val"
                      :class="{ 'mobile-cell-val--zero': getCellValue(capteur.id, date, maladie) === 0 }"
                    >{{ getCellValue(capteur.id, date, maladie) }}</span>
                    <span v-else class="mobile-cell-empty">—</span>
                  </template>
                </template>

                <!-- Desktop: input box with keyboard navigation -->
                <div v-else class="cell-content">
                  <!-- Active cell (value exists or is being edited) -->
                  <template v-if="getCellValue(capteur.id, date, maladie) !== null">
                    <input
                      :id="cellKey(capteur.id, date, maladie)"
                      type="text"
                      inputmode="numeric"
                      size="1"
                      class="spore-input"
                      :class="{
                        'input-zero': getCellValue(capteur.id, date, maladie) === 0,
                        'input-higher': (getCellValue(capteur.id, date, maladie) ?? 0) > 0,
                        'input-pending': isPending(capteur.id, date, maladie)
                      }"
                      :value="getCellValue(capteur.id, date, maladie)"
                      :readonly="!canEdit"
                      @input="onCellInput(capteur.id, date, maladie, $event)"
                      @keydown="onCellKeyDown($event, ci, di, mi)"
                      @focus="($event.target as HTMLInputElement).select()"
                    />
                    <span v-if="isPending(capteur.id, date, maladie)" class="unsaved-dot"/>
                    <button
                      v-if="canEdit"
                      class="delete-capture-btn"
                      tabindex="-1"
                      :aria-label="`Supprimer ${maladiesDisplay[mi]} pour ${capteur.nom}`"
                      @click.stop="deleteCapture(capteur.id, date, maladie)"
                    >
                      <v-icon icon="M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z" size="11"/>
                    </button>
                  </template>

                  <!-- Empty cell — show + button if can edit -->
                  <template v-else-if="canEdit">
                    <button
                      :id="`btn-${cellKey(capteur.id, date, maladie)}`"
                      class="add-cell-btn"
                      :aria-label="`Ajouter ${maladiesDisplay[mi]} pour ${capteur.nom}`"
                      @click="activateCell(capteur.id, date, maladie)"
                      @keydown="onCellKeyDown($event, ci, di, mi)"
                    >
                      <v-icon icon="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" size="14"/>
                    </button>
                  </template>
                  <template v-else>
                    <span class="text-caption text-disabled">—</span>
                  </template>
                </div>
              </td>
            </template>
          </tr>
        </tbody>
      </table>

      <div v-if="capteurs.length === 0 && !isLoading" class="d-flex justify-center align-center pt-8">
        <v-icon icon="M22.1 21.5L2.4 1.7L1.1 3L4.1 6C2.8 7.6 2 9.7 2 12C2 13.1 2.9 14 4 14H12.1L13.1 15H9L8 18.5L7.7 19.5C7.4 20.6 8.1 21.7 9.1 21.9L9.7 22H14.4C15.5 22 16.4 21.1 16.4 20L16.3 19.5L16 18.5L15.8 17.6L20.9 22.7L22.1 21.5M4 12C4 10.3 4.6 8.7 5.5 7.4L6.2 8.1C6.1 8.4 6 8.7 6 9C6 10.1 6.9 11 8 11C8.3 11 8.6 10.9 8.9 10.8L10.1 12H4M9.7 20L10 19L10.6 17H13.6L14.2 19L14.5 20H9.7M16 11C14.9 11 14 10.1 14 9S14.9 7 16 7 18 7.9 18 9 17.1 11 16 11M8.2 5L6.7 3.5C8.3 2.6 10.1 2 12 2C17.5 2 22 6.5 22 12C22 13.1 21.1 14 20 14H17.2L15.2 12H20C20 8.3 17.4 5.1 14 4.2C13.9 5.2 13 6 12 6S10.1 5.2 10 4.2C9.4 4.4 8.7 4.7 8.2 5Z" size="48" class="text-disabled me-3"/>
        <span class="text-medium-emphasis">Aucun capteur configuré</span>
      </div>
    </div>
  </v-card>

  <!-- Culture dialog -->
  <v-dialog v-model="showDialogCulture" max-width="480px" :fullscreen="$vuetify.display.xs">
    <v-card rounded="xl">
      <v-card-title class="d-flex justify-space-between align-center pt-4 px-5">
        <div class="d-flex align-center" style="gap: 10px;">
          <v-avatar color="primary" variant="tonal" size="36" rounded="lg">
            <v-icon icon="M17,8C8,10 5.9,16.17 3.82,21.34L5.71,22L6.66,19.7C7.14,19.87 7.64,20 8,20C19,20 22,3 22,3C21,5 14,5.25 9,6.25C4,7.25 2,11.5 2,13.5C2,15.5 3.75,17.25 3.75,17.25C7,8 17,8 17,8Z" size="20"/>
          </v-avatar>
          <span class="text-h6 font-weight-bold">Sélectionner une culture</span>
        </div>
        <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" @click="showDialogCulture = false"/>
      </v-card-title>
      <v-divider class="mt-2 mb-4"/>
      <v-card-text class="pb-5">
        <v-row dense>
          <v-col v-for="culture in cultures" :key="culture" cols="12" sm="6">
            <v-btn
              class="culture-select-btn"
              color="primary"
              variant="tonal"
              block
              rounded="lg"
              height="48"
              @click="confirmCulture(culture)">
              {{ culture }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>

  <DialogConfirm ref="dialogConfirm"/>
</template>

<style scoped>
.capteur-nom-link {
  color: rgba(var(--v-theme-on-surface), 0.55);
  transition: color 0.15s;
}
.capteur-nom-link--clickable {
  cursor: pointer;
}
.capteur-nom-link--clickable:hover {
  color: rgb(var(--v-theme-teal));
  text-decoration: underline;
}
.culture-chip {
  transition: opacity 0.15s;
}
.culture-chip:hover {
  opacity: 0.78;
}
.culture-select-btn {
  text-transform: capitalize;
  font-weight: 600;
  letter-spacing: 0.01em;
}

/* ── Save button pulse ────────────────────── */
.save-btn {
  animation: pulse-green 2s ease-in-out infinite;
}
@keyframes pulse-green {
  0%, 100% { box-shadow: 0 0 0 0 rgba(var(--v-theme-success), 0.45); }
  50%       { box-shadow: 0 0 0 6px rgba(var(--v-theme-success), 0); }
}

/* ── Table ────────────────────────────────── */
.spores-table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  --row1-h: 26px;
}

.spores-table th,
.spores-table td {
  padding: 2px 4px 2px 6px;
  text-align: center;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-right: 1px solid rgba(var(--v-border-color), calc(var(--v-border-opacity) * 0.7));
}
.spores-table th:last-child,
.spores-table td:last-child {
  border-right: none;
}
.spores-table thead th {
  position: sticky;
  z-index: 10;
  font-size: 0.70rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  white-space: nowrap;
}

/* Date group header */
.date-header {
  top: 0;
  height: var(--row1-h);
  padding-top: 0;
  padding-bottom: 0;
  background: rgb(var(--v-theme-primary));
  color: #fff !important;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  border-bottom: 2px solid rgba(255, 255, 255, 0.25) !important;
}

/* Capteur fixed column */
.col-fixed {
  position: sticky;
  z-index: 11;
}
.col-capteur {
  left: 0;
  min-width: 160px;
  text-align: left !important;
}
.spores-table thead th.col-capteur {
  z-index: 12;
  background: rgb(var(--v-theme-surface));
  border-right: 2px solid rgba(var(--v-border-color), calc(var(--v-border-opacity) * 1.5));
}
.col-capteur-r1 { top: 0; height: var(--row1-h); padding-top: 0; padding-bottom: 0; border-bottom: none; }
.col-capteur-r2 {
  top: var(--row1-h);
  border-bottom: 2px solid rgba(var(--v-theme-primary), 0.25) !important;
}
.spores-table tbody td.col-capteur {
  background: rgb(var(--v-theme-surface));
  border-right: 2px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

/* Category sub-header */
.category-header {
  top: var(--row1-h);
  background: color-mix(in srgb, rgb(var(--v-theme-primary)) 12%, rgb(var(--v-theme-surface)));
  color: rgb(var(--v-theme-primary));
  font-size: 0.60rem !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  border-bottom: 2px solid rgba(var(--v-theme-primary), 0.25) !important;
}

/* Row accent for current site */
.row-current-site td.col-capteur {
  border-left: 3px solid rgb(var(--v-theme-primary)) !important;
}

/* Row highlight (URL ?capteur= param) */
.row-highlighted td {
  background-color: rgba(var(--v-theme-warning), 0.12);
}
.row-highlighted td.col-fixed {
  background-color: color-mix(in srgb, rgb(var(--v-theme-warning)) 12%, rgb(var(--v-theme-surface))) !important;
}
.row-highlighted td.col-capteur {
  box-shadow: inset 3px 0 0 rgb(var(--v-theme-warning));
}

/* Row hover — keep sticky cells opaque */
.data-row:hover td {
  background: rgba(var(--v-theme-on-surface), 0.04) !important;
}
.data-row:hover td.col-fixed {
  background: rgb(var(--v-theme-surface)) !important;
}
.data-row:last-child td {
  border-bottom: none;
}

.capteur-cell {
  padding: 3px 8px;
  white-space: nowrap;
}
.data-cell {
  padding: 0;
  position: relative;
}
.data-cell--zero {
  background: rgba(var(--v-theme-on-surface), 0.05);
}
.data-cell--higher {
  background: rgba(var(--v-theme-warning), 0.12);
}
/* Pending (unsaved) — orange border on the cell */
.data-cell:has(.input-pending) {
  box-shadow: inset 0 0 0 2px rgb(var(--v-theme-warning));
}
/* Active / focused — blue border overrides orange */
.data-cell:focus-within {
  box-shadow: inset 0 0 0 2px rgb(var(--v-theme-primary));
  z-index: 2;
}

/* Cell content */
.cell-content {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
}

/* Native number input for fast keyboard entry */
.spore-input {
  flex: 1;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  text-align: center;
  font-size: 0.78rem;
  border: none;
  padding: 5px 2px;
  background: transparent;
  color: inherit;
  outline: none;
}
.input-zero {
  color: rgba(var(--v-theme-on-surface), 0.45);
}
.input-higher {
  color: rgb(var(--v-theme-warning));
  font-weight: 700;
}

/* + button for empty cells */
.add-cell-btn {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1px dashed rgba(var(--v-theme-on-surface), 0.25);
  background: transparent;
  color: rgba(var(--v-theme-on-surface), 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.add-cell-btn:hover {
  background: rgba(var(--v-theme-success), 0.12);
  border-color: rgb(var(--v-theme-success));
  color: rgb(var(--v-theme-success));
}

/* Unsaved dot — absolute corner indicator */
.unsaved-dot {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: rgb(var(--v-theme-warning));
  pointer-events: none;
}

/* Delete capture button */
.delete-capture-btn {
  position: absolute;
  bottom: -2px;
  left: -2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: none;
  background: rgb(var(--v-theme-error));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s;
  padding: 0;
  z-index: 1;
}
.data-cell:hover .delete-capture-btn,
.data-cell:focus-within .delete-capture-btn {
  opacity: 1;
}
.delete-capture-btn:hover {
  filter: brightness(1.15);
}

/* ── Mobile colored cells ────────────────── */
.mobile-cell--positive {
  background: rgba(var(--v-theme-warning), 0.18);
}
.mobile-cell--zero {
  background: rgba(var(--v-theme-on-surface), 0.05);
}
.mobile-cell--pending {
  box-shadow: inset 0 0 0 2px rgb(var(--v-theme-warning));
}
.mobile-cell-val {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  color: rgb(var(--v-theme-warning));
  text-align: center;
  line-height: 1;
  padding: 3px 0;
}
.mobile-cell-val--zero {
  color: rgba(var(--v-theme-on-surface), 0.4);
  font-weight: 400;
}
.mobile-cell-empty {
  display: block;
  font-size: 0.65rem;
  color: rgba(var(--v-theme-on-surface), 0.2);
  text-align: center;
  line-height: 1;
  padding: 3px 0;
}
.mobile-spore-input {
  display: block;
  width: 100%;
  min-height: 26px;
  text-align: center;
  font-size: 0.78rem;
  font-weight: 700;
  color: rgb(var(--v-theme-warning));
  background: rgba(var(--v-theme-warning), 0.15);
  border: 2px solid rgb(var(--v-theme-warning));
  border-radius: 2px;
  padding: 2px 0;
  outline: none;
}

@media (max-width: 959px) {
  .col-capteur { min-width: 130px; }
}

/* ── Mobile: collapsible header ──────────── */
.page-header {
  flex-shrink: 0;
  overflow: hidden;
  max-height: 400px;
  transition: max-height 0.3s ease, opacity 0.25s ease, padding 0.25s ease;
}
.page-header.header-collapsed {
  max-height: 0 !important;
  opacity: 0;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  pointer-events: none;
}

/* ── Mobile: compact capteur cell ────────── */
.capteur-cell-mobile {
  padding: 2px 0;
}
@media (max-width: 599px) {
  .col-capteur {
    min-width: 105px;
    max-width: 130px;
  }
  .spore-input {
    font-size: 0.72rem;
  }
}
</style>
